/* ==========================================================================
   campaign-discount.js  [カスタマイズ追記] 追記日: 2026-09-16
   --------------------------------------------------------------------------
   キャンペーン割引率（テーマ設定「キャンペーン割引」）のクライアント側エンジン。

   【役割】
   snippets/campaign-discount.liquid が出力する
     <script type="application/json" id="campaign-discount-data">
   を読み、端末の現在時刻（JST に固定）で同じルールを再評価して data-cd 属性の要素を
   書き換える。Liquid の描画は Shopify のページキャッシュで最大数分古くなるため、
   切り替え時刻の前後だけ JS が補正する。1分ごとと日付境界で再評価する。

   【枠の定義（JSON の slots[]）】
     repeat: none    → start / end（YYYY-MM-DD、両方必須）
             weekly  → weekdays[]（月=1…日=7）＋ 任意の start / end
             monthly → monthFrom / monthTo（日）＋ 任意の start / end
             yearly  → yStart / yEnd（MM-DD、年またぎ可）

   【マークアップ】
     <span data-cd="rate"></span>                    … 率（数字のみ）
     <span data-cd="label"></span>                   … キャンペーン名
     <span data-cd="price" data-cd-base="286000"></span>
                                                    … 割引後価格。base は cents（¥2,860 → 286000）。
                                                      割引後は 1円未満切り捨て → ¥2,002
     <span data-cd="off" data-cd-base="286000"></span>
                                                    … 割引額（¥858）
     <span data-cd="end"></span>                     … 終了日（9月20日まで）
     <span data-cd="countdown"></span>               … 残り時間（あと3日 04:12:33）。毎秒更新
     <span data-cd="code"></span>                    … 初回クーポンコード（枠の code、無ければ通常時）
     <div data-cd-code data-coupon-code="WLCM20">    … data-coupon-code 属性を現在のコードに書き換える
     <div data-cd-banner …>（campaign-banner.liquid） … 期間の境目で枠のバナー／フォールバックに差し替える
                                                      data-cd-banner-kind="hero" ならヒーロー（slots[].hero）
     <div data-cd-show="active"></div>               … キャンペーン中だけ表示
     <div data-cd-show="inactive"></div>             … 通常時だけ表示
     <div data-cd-show="slot-2"></div>               … 枠2が採用されているときだけ表示
     <div data-cd-show="discount"></div>             … 率が 0 より大きいとき（通常率含む）だけ表示

   【検証】
     URL に ?cd_now=2026-12-25 を付けると JS 側だけその日付で評価する（Liquid は実日付）。
     snippets/campaign-debug.liquid を置いたページで Liquid / JS の値を並べて確認できる。

   【他スクリプトから】
     window.CampaignDiscount.get()   → { rate, active, slot, label, end, base, code, bannerSlot, heroSlot }
     document.addEventListener('campaign-discount:change', e => e.detail)
   ========================================================================== */
(function () {
  if (window.CampaignDiscount) return;

  var JST_OFFSET_MIN = 9 * 60;
  var fmtYen = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 });

  function readConfig() {
    var el = document.getElementById('campaign-discount-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // 端末時刻を JST の年月日・曜日に変換する（端末のタイムゾーンに依存しない）
  function jstParts(date) {
    var utc = date.getTime() + date.getTimezoneOffset() * 60000;
    var j = new Date(utc + JST_OFFSET_MIN * 60000);
    return {
      y: j.getFullYear(), m: j.getMonth() + 1, d: j.getDate(),
      dow: (j.getDay() + 6) % 7 + 1,            // 月=1 … 日=7（Liquid の %u と同じ）
      ymd: j.getFullYear() * 10000 + (j.getMonth() + 1) * 100 + j.getDate(),
      mmdd: (j.getMonth() + 1) * 100 + j.getDate()
    };
  }
  function ymdOf(str) { return str && str.length === 10 ? parseInt(str.replace(/-/g, ''), 10) : 0; }
  function mmddOf(str) { return str && str.length === 5 ? parseInt(str.replace('-', ''), 10) : 0; }   // "12-25" → 1225
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }
  function endOfDayIso(y, m, d) { return y + '-' + pad2(m) + '-' + pad2(d) + 'T23:59:59+09:00'; }

  // Liquid（snippets/campaign-discount.liquid）と同じルール
  // 検証用: URL に ?cd_now=YYYY-MM-DD を付けると JS 側だけその日付で評価する
  // （Liquid の描画は実日付のまま → JS 補正の動きを確認できる）。テーマ設定の上書きが優先。
  function urlDebugNow() {
    var m = /[?&]cd_now=(\d{4}-\d{2}-\d{2})/.exec(window.location.search);
    return m ? m[1] : '';
  }

  function evaluate(cfg, now) {
    var dbg = cfg.debugNow || urlDebugNow();
    var t = dbg ? jstParts(new Date(dbg.slice(0, 10) + 'T12:00:00+09:00')) : jstParts(now);
    var best = null;
    (cfg.slots || []).forEach(function (s) {
      if (!s.enabled || !(s.rate > 0)) return;
      var startYmd = ymdOf(s.start), endYmd = ymdOf(s.end);
      var inWindow = !(startYmd > 0 && t.ymd < startYmd) && !(endYmd > 0 && t.ymd > endYmd);
      var active = false, end = '';
      switch (s.repeat) {
        case 'none':
          if (startYmd > 0 && endYmd > 0 && inWindow) { active = true; end = s.end + 'T23:59:59+09:00'; }
          break;
        case 'weekly':
          if (inWindow && (s.weekdays || []).indexOf(t.dow) !== -1) { active = true; end = endOfDayIso(t.y, t.m, t.d); }
          break;
        case 'monthly':
          if (inWindow && t.d >= s.monthFrom && t.d <= s.monthTo) {
            active = true;
            end = endOfDayIso(t.y, t.m, Math.min(s.monthTo, daysInMonth(t.y, t.m)));
          }
          break;
        case 'yearly': {
          // 毎年: yStart / yEnd は "MM-DD"
          var sm = mmddOf(s.yStart), em = mmddOf(s.yEnd);
          if (sm > 0 && em > 0) {
            var hit, endYear = t.y;
            if (sm <= em) { hit = t.mmdd >= sm && t.mmdd <= em; }
            else { hit = t.mmdd >= sm || t.mmdd <= em; if (t.mmdd >= sm) endYear = t.y + 1; }
            if (hit) { active = true; end = endYear + '-' + s.yEnd + 'T23:59:59+09:00'; }
          }
          break;
        }
      }
      if (active && (!best || s.rate > best.rate)) {
        best = { rate: s.rate, slot: s.i, label: s.label || '', end: end, code: s.code || '', banner: s.banner || null, hero: s.hero || null };
      }
    });
    var baseCode = cfg.baseCode || '';
    return best
      ? { rate: best.rate, active: true, slot: best.slot, label: best.label, end: best.end, base: cfg.base || 0,
          code: best.code || baseCode, bannerSlot: best.banner ? best.slot : 0, heroSlot: best.hero ? best.slot : 0 }
      : { rate: cfg.base || 0, active: false, slot: 0, label: '', end: '', base: cfg.base || 0,
          code: baseCode, bannerSlot: 0, heroSlot: 0 };
  }

  function slotById(cfg, i) {
    var slots = cfg.slots || [];
    for (var k = 0; k < slots.length; k++) if (slots[k].i === i) return slots[k];
    return null;
  }

  function escAttr(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  // 枠のバナー定義（JSON の slots[].banner / slots[].hero）から <picture> を組む。campaign-banner.liquid と同じ構造
  function bannerHtml(b, sizes, isHero) {
    var html = '<picture>';
    if (b.spSet) {
      html += '<source media="(max-width: 749px)" srcset="' + escAttr(b.spSet) + '" sizes="100vw"' +
        (b.spW ? ' width="' + b.spW + '" height="' + b.spH + '"' : '') + '>';
    }
    html += '<img class="cd-banner__img" src="' + escAttr(b.pc) + '" srcset="' + escAttr(b.pcSet) + '"' +
      ' sizes="' + escAttr(sizes || '100vw') + '"' +
      (b.w ? ' width="' + b.w + '" height="' + b.h + '"' : '') +
      ' alt="' + escAttr(b.alt) + '" loading="' + (isHero ? 'eager' : 'lazy') + '" decoding="async"' + (isHero ? ' fetchpriority="high"' : '') + '></picture>';
    if (b.link) html = '<a class="cd-banner__link" href="' + escAttr(b.link) + '">' + html + '</a>';
    return html;
  }

  // [data-cd-banner]（campaign-banner.liquid の出力）を現在の枠に合わせて差し替える
  //   data-cd-banner-kind="hero" は slots[].hero / state.heroSlot、それ以外は slots[].banner / state.bannerSlot
  function renderBanners() {
    document.querySelectorAll('[data-cd-banner]:not([data-cd-banner-static])').forEach(function (el) {
      var isHero = el.getAttribute('data-cd-banner-kind') === 'hero';
      var rendered = parseInt(el.getAttribute('data-cd-banner-slot'), 10) || 0;
      var desired = isHero ? state.heroSlot : state.bannerSlot;
      if (rendered === desired) return;
      var tpl = el.querySelector('template[data-cd-banner-fallback]');
      var html = '';
      if (desired > 0) {
        var s = slotById(cfg, desired);
        var def = s ? (isHero ? s.hero : s.banner) : null;
        if (def) html = bannerHtml(def, el.getAttribute('data-cd-banner-sizes'), isHero);
      } else if (tpl) {
        html = tpl.innerHTML;
      }
      // template 以外の子を入れ替える
      Array.prototype.slice.call(el.children).forEach(function (c) { if (c !== tpl) el.removeChild(c); });
      if (html.trim()) {
        el.insertAdjacentHTML('afterbegin', html);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
      el.setAttribute('data-cd-banner-slot', String(desired));
    });
  }

  var state = null;
  var cfg = null;

  function setText(el, text) { if (el.textContent !== text) el.textContent = text; }

  function priceAfter(baseCents, rate) {
    // 1円未満切り捨て（cents → 円 → 割引 → floor）
    var yen = Math.floor(baseCents / 100);
    return Math.floor(yen * (100 - rate) / 100);
  }

  function formatEnd(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    var p = jstParts(d);
    return p.m + '月' + p.d + '日まで';
  }

  function formatCountdown(iso, now) {
    if (!iso) return '';
    var ms = new Date(iso).getTime() - now.getTime();
    if (isNaN(ms) || ms <= 0) return '終了';
    var s = Math.floor(ms / 1000);
    var days = Math.floor(s / 86400); s -= days * 86400;
    var h = Math.floor(s / 3600); s -= h * 3600;
    var m = Math.floor(s / 60); s -= m * 60;
    return 'あと' + (days > 0 ? days + '日 ' : '') + pad2(h) + ':' + pad2(m) + ':' + pad2(s);
  }

  function render(now) {
    if (!state) return;
    document.querySelectorAll('[data-cd]').forEach(function (el) {
      var kind = el.getAttribute('data-cd');
      var base = parseInt(el.getAttribute('data-cd-base'), 10) || 0;
      switch (kind) {
        case 'rate': setText(el, String(state.rate)); break;
        case 'label': setText(el, state.label); break;
        case 'price': setText(el, fmtYen.format(priceAfter(base, state.rate))); break;
        case 'off': setText(el, fmtYen.format(Math.floor(base / 100) - priceAfter(base, state.rate))); break;
        case 'end': setText(el, formatEnd(state.end)); break;
        case 'countdown': setText(el, formatCountdown(state.end, now)); break;
        case 'code': setText(el, state.code); break;
      }
    });
    // 初回クーポンの取得ボタンなど：data-coupon-code 属性を現在のコードに合わせる
    document.querySelectorAll('[data-cd-code]').forEach(function (el) {
      if (state.code && el.getAttribute('data-coupon-code') !== state.code) el.setAttribute('data-coupon-code', state.code);
    });
    renderBanners();
    document.querySelectorAll('[data-cd-show]').forEach(function (el) {
      var cond = el.getAttribute('data-cd-show');
      var show;
      if (cond === 'active') show = state.active;
      else if (cond === 'inactive') show = !state.active;
      else if (cond === 'discount') show = state.rate > 0;
      else if (cond.indexOf('slot-') === 0) show = state.slot === parseInt(cond.slice(5), 10);
      else show = true;
      if (el.hidden === show) el.hidden = !show;
    });
  }

  function tick() {
    if (!cfg) { cfg = readConfig(); if (!cfg) return; }
    var now = new Date();
    var next = evaluate(cfg, now);
    var changed = !state || JSON.stringify(next) !== JSON.stringify(state);
    state = next;
    render(now);
    if (changed) {
      document.dispatchEvent(new CustomEvent('campaign-discount:change', { detail: state }));
    }
  }

  window.CampaignDiscount = {
    get: function () { if (!state) tick(); return state; },
    refresh: tick
  };

  function start() {
    tick();
    // 秒表示（countdown）のため 1 秒ごとに描画。評価は毎分（tick 内で判定し直す）
    setInterval(function () {
      if (document.querySelector('[data-cd="countdown"]')) render(new Date());
    }, 1000);
    setInterval(tick, 60000);
    // セクション再描画（カート更新など）で要素が差し替わった場合に追従
    new MutationObserver(function (m) {
      for (var i = 0; i < m.length; i++) {
        if (m[i].addedNodes && m[i].addedNodes.length) { render(new Date()); break; }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();